<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="sn-question">
        <span class="sn-bar sn-bar--xl"></span>
      </div>
      <div class="sn-wall">
        <div
          v-for="(n, i) in notes"
          :key="i"
          class="sn-note"
          :class="['--c-' + n.color, { '--landing': i === 0 }]"
          :style="{ '--rot': n.rot + 'deg', '--note-i': i }"
        >
          <span class="sn-text">{{ n.text }}</span>
        </div>
      </div>
    </template>

    <template #audience>
      <span class="sn-phone-q"></span>
      <div class="sn-phone-note">
        <span class="sn-phone-text">Try retros monthly<span class="sn-caret">|</span></span>
      </div>
      <button type="button" class="sn-phone-add" tabindex="-1">
        <span class="material-symbols-rounded">add</span>
        Stick it
      </button>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewStickyNotes',
  components: { PreviewFrame },
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
  },
  data() {
    return {
      notes: [
        { text: 'Retros', color: 'pink', rot: -3 },
        { text: 'Pair fri', color: 'yellow', rot: 2 },
        { text: 'Demos', color: 'blue', rot: -1 },
        { text: 'Lunch learn', color: 'green', rot: 3 },
        { text: 'Office hr', color: 'pink', rot: -2 },
        { text: 'Hack day', color: 'yellow', rot: 1 },
        { text: 'Onboard', color: 'blue', rot: -2 },
        { text: 'Mentoring', color: 'green', rot: 2 },
      ],
    }
  },
})
</script>

<style scoped lang="scss">
.sn-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.2);
  width: 70%;
}
.sn-question {
  padding-bottom: 6px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.sn-wall {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  padding: 4px;
  background: rgba(15, 15, 20, 0.03);
  border-radius: 6px;
  align-content: start;
}
.sn-note {
  border-radius: 3px;
  padding: 6px 5px;
  min-height: 40px;
  transform: rotate(var(--rot, 0deg));
  box-shadow: 0 1px 2px rgba(15, 15, 20, 0.08);
  display: flex; align-items: center; justify-content: center;
}
.sn-note.--c-pink   { background: #FFD6E7; }
.sn-note.--c-yellow { background: #FFF4C2; }
.sn-note.--c-blue   { background: #D6E8FF; }
.sn-note.--c-green  { background: #D6F3DA; }
.sn-text {
  font-size: 8px; font-weight: 700;
  color: rgba(15, 15, 20, 0.7);
  text-align: center;
  line-height: 1.15;
}

.sn-note.--landing {
  animation: sn-stick 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}

/* ---- Audience phone ---- */
.sn-phone-q {
  display: block; height: 8px; border-radius: 999px;
  width: 70%; background: rgba(15, 15, 20, 0.18);
  margin-bottom: 4px;
}
.sn-phone-note {
  background: #FFF4C2;
  border-radius: 6px;
  padding: 18px 10px;
  min-height: 60px;
  transform: rotate(-1.5deg);
  box-shadow: 0 1px 3px rgba(15, 15, 20, 0.12);
}
.sn-phone-text {
  font-size: 11px; font-weight: 700;
  color: #8B6914;
}
.sn-caret { color: #8B6914; animation: sn-blink 1s steps(2) infinite; }
.sn-phone-add {
  appearance: none; border: none;
  background: var(--accent); color: white;
  font-family: inherit; font-weight: 700; font-size: 12px;
  border-radius: 8px;
  padding: 8px 12px;
  display: flex; align-items: center; justify-content: center; gap: 4px;
  cursor: pointer;
  animation: sn-tap 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}
.sn-phone-add .material-symbols-rounded { font-size: 14px; }

@keyframes sn-stick {
  0%, 12% { transform: rotate(var(--rot)) translateY(-20px) scale(0.5); opacity: 0; }
  35%     { transform: rotate(var(--rot)) translateY(2px) scale(1.08); opacity: 1; }
  45%     { transform: rotate(var(--rot)) translateY(0) scale(0.97); }
  55%, 92% { transform: rotate(var(--rot)) translateY(0) scale(1); opacity: 1; }
  100%    { transform: rotate(var(--rot)) translateY(0) scale(1); opacity: 1; }
}
@keyframes sn-blink { 0% { opacity: 1; } 100% { opacity: 0; } }
@keyframes sn-tap {
  0%, 60%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 35%, transparent); }
  72%       { box-shadow: 0 0 0 8px color-mix(in srgb, var(--accent) 0%, transparent); }
  100%      { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent); }
}

@media (prefers-reduced-motion: reduce) {
  .sn-note.--landing, .sn-caret, .sn-phone-add { animation: none; }
}
</style>
